import { cloneDeep } from "lodash";
import {
  format as formatDate,
  parseISO as parseISODate,
  addDays,
  addHours,
  addMinutes,
} from "date-fns";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import * as AdvancedMessageEntitiesTypes from "../AdvancedMessageField/AdvancedMessageEntitiesTypes.constants";
import * as PostEditorTabsTypes from "./PostEditorTabsTypes.constants";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import { getSocialProviderBySocialType } from "../../../_utils/socialProvider";
import {
  canAddMentions,
  canAddMentionsWithSpaces,
  canAddScraper,
  canEditScraperDetails,
  canAddCarousel,
  getAdditionalTypesFields,
  getBaseMaxComments,
  getBaseMaxPhotos,
  getBaseMaxVideos,
  getMaxCarouselItems,
  getMaxComments,
  getMaxHashtags,
  getMaxMentions,
  getMaxMessageChars,
  getMaxPhotos,
  getMaxVideos,
  haveHighlightedAtMentions,
  haveHighlightedHashtags,
  haveHighlightedLinks,
  haveMessageLinkPreview,
  getBaseMaxPhotosVideos,
  getMaxPhotosVideos,
} from "./restrictions";
import { getImageMeta, getVideoMeta } from "./utils";

export const photosVideosSortFunc = (a, b) => {
  if (a.order === b.order) return 0;
  if (b.order === undefined) return -1;
  return a.order - b.order;
};

export const photosVideosDataSortFunc = (a, b) => {
  if (a.data?.order === b.data?.order) return 0;
  if (b.data?.order === undefined) return -1;
  return a.data?.order - b.data?.order;
};

export const getPhotoVideoMaxOrder = (photos, videos) =>
  Math.max(
    0,
    ...[...(photos || []), ...(videos || [])]
      .map((item) => item.order)
      .filter((item) => item >= 0)
  );

export const getFixedPhotosVideosOrder = (photos, videos) => {
  let orderCounter = 0;
  const items = [
    ...(videos || []).map((item) => ({
      field: "videos",
      data: item,
    })),
    ...(photos || []).map((item) => ({
      field: "photos",
      data: item,
    })),
  ].sort(photosVideosDataSortFunc);
  const itemsFixed = items.map((item) => {
    orderCounter = orderCounter + 1;

    return {
      ...item,
      data: {
        ...item.data,
        order: orderCounter,
      },
    };
  });

  return {
    videos: itemsFixed
      .filter((item) => item.field === "videos")
      .map((item) => item.data),
    photos: itemsFixed
      .filter((item) => item.field === "photos")
      .map((item) => item.data),
    items,
  };
};

const slicePhotosVideosByOrder = (photos, videos, maxPhotosVideos) => {
  if (videos.length + photos.length <= maxPhotosVideos) {
    return { photos, videos };
  }

  const itemsToRemove = [
    ...videos.map((item, index) => ({
      field: "videos",
      index,
      order: item.order || undefined,
    })),
    ...photos.map((item, index) => ({
      field: "photos",
      index,
      order: item.order || undefined,
    })),
  ]
    .sort(photosVideosSortFunc)
    .reverse()
    .slice(0, videos.length + photos.length - maxPhotosVideos);

  const photosIndexesToRemove = itemsToRemove
    .filter((item) => item.field === "photos")
    .map((item) => item.index);
  const videosIndexesToRemove = itemsToRemove
    .filter((item) => item.field === "videos")
    .map((item) => item.index);

  return {
    photos: photos.filter((item, index) =>
      photosIndexesToRemove.includes(index)
    ),
    videos: videos.filter((item, index) =>
      videosIndexesToRemove.includes(index)
    ),
  };
};

export const getTabsForAccounts = (accounts = [], accountsIds = []) => {
  const selectedAccounts = accounts?.filter((account) =>
    accountsIds.includes(account.id)
  );
  const types = [];

  selectedAccounts.forEach((account) => {
    const provider = getSocialProviderBySocialType(account.social_type);

    if (provider && !types.includes(provider)) {
      types.push(provider);
    }
  });

  return types.sort();
};

export const getEntitiesMapForTab = (entitiesMap, filterTab) => {
  if (!Array.isArray(entitiesMap)) {
    return [];
  }

  return cloneDeep(entitiesMap)
    .filter(
      (item) =>
        filterTab === PostEditorTabsTypes.BASE ||
        item.type !== AdvancedMessageEntitiesTypes.MENTION ||
        item.source === filterTab
    )
    .sort((a, b) => (a.offset < b.offset ? 1 : -1));
};

export const getAdditionalForTab = (additional, filterTab, postType) => {
  if (!Array.isArray(additional)) {
    return [];
  }

  const additionalCopy = cloneDeep(additional);
  const additionalTypesFields = getAdditionalTypesFields(
    filterTab,
    postType,
    additionalCopy
  );
  const data = [];

  Object.keys(additionalTypesFields).forEach((type) => {
    const fields = {};

    Object.keys(additionalTypesFields[type]).forEach((field) => {
      fields[field] = additionalTypesFields[type][field].value;
    });

    data.push({
      ...fields,
      source: filterTab,
      type,
    });
  });

  return data;
};

export const getAccountsForTab = (accounts, accountsIds, tab) => {
  const tabAccounts = accounts?.filter(
    (account) =>
      accountsIds.includes(account.id) &&
      getSocialProviderBySocialType(account.social_type) === tab
  );

  return tabAccounts || [];
};

export const getSeparatedData = (data = {}) => {
  const {
    message,
    scraper,
    photos,
    videos,
    comments,
    additional,
    entitiesMap,
  } = cloneDeep(data);

  return {
    message: message || "",
    scraper: scraper || null,
    photos: photos || [],
    videos: videos || [],
    comments: comments || [],
    additional: additional || [],
    entitiesMap: entitiesMap || [],
  };
};

export const getStateForTab = (data, tab) => {
  const state = cloneDeep(data);

  state.entitiesMap = getEntitiesMapForTab(data.entitiesMap, tab);

  if (tab !== PostEditorTabsTypes.BASE) {
    const postType = state.additional?.find(
      (item) => item.type === PostAdditionalTypes.POST_TYPE
    )?.value;

    if (state?.videos?.length) {
      const max = getMaxVideos(tab, postType);

      if (max === 0) {
        state.videos = [];
      } else if (max > 0) {
        state.videos = state.videos.slice(0, max);
      }
    } else {
      state.videos = [];
    }

    if (state?.photos?.length) {
      const max = getMaxPhotos(tab, postType);

      if (max === 0) {
        state.photos = [];
      } else if (max > 0) {
        state.photos = state.photos.slice(0, max);
      }
    } else {
      state.photos = [];
    }

    const maxPhotosVideos = getMaxPhotosVideos(tab, postType);

    if (maxPhotosVideos >= 0) {
      const slicedPhotosVideos = slicePhotosVideosByOrder(
        state.photos,
        state.videos,
        maxPhotosVideos
      );

      state.videos = slicedPhotosVideos.videos;
      state.photos = slicedPhotosVideos.photos;
    } else if (state?.videos?.length) {
      state.photos = [];
    }

    if (state?.message && getMaxMessageChars(tab, postType) === 0) {
      state.message = "";
    }

    if (state?.scraper && !canAddScraper(tab, postType)) {
      state.scraper = null;
    }

    if (state?.comments?.length) {
      const max = getMaxComments(tab, postType);

      if (max === 0) {
        state.comments = [];
      } else if (max > 0) {
        state.comments = state.comments.slice(0, max);
      }
    }

    state.additional = getAdditionalForTab(data.additional, tab, postType);
  } else {
    if (state?.videos?.length) {
      const max = getBaseMaxVideos();

      if (max === 0) {
        state.videos = [];
      } else if (max > 0) {
        state.videos = state.videos.slice(0, max);
      }
    } else {
      state.videos = [];
    }

    if (state?.photos?.length) {
      const max = getBaseMaxPhotos();

      if (max === 0) {
        state.photos = [];
      } else if (max > 0) {
        state.photos = state.photos.slice(0, max);
      }
    } else {
      state.photos = [];
    }

    const maxPhotosVideos = getBaseMaxPhotosVideos();

    if (maxPhotosVideos >= 0) {
      const slicedPhotosVideos = slicePhotosVideosByOrder(
        state.photos,
        state.videos,
        maxPhotosVideos
      );

      state.videos = slicedPhotosVideos.videos;
      state.photos = slicedPhotosVideos.photos;
    } else if (state?.videos?.length) {
      state.photos = [];
    }

    if (state?.comments?.length) {
      const max = getBaseMaxComments();

      if (max === 0) {
        state.comments = [];
      } else if (max > 0) {
        state.comments = state.comments.slice(0, max);
      }
    }
  }

  if (state?.videos?.length || state?.photos?.length) {
    state.scraper = null;
    state.additional = state.additional?.filter(
      (item) => item.type !== PostAdditionalTypes.CAROUSEL
    );
  }

  return state;
};

export const getConfigForTab = (tab, postType, enabledTabs) => {
  if (tab === PostEditorTabsTypes.BASE) {
    const customizedTabs = enabledTabs.filter(
      (item) => item !== PostEditorTabsTypes.BASE
    );
    const customizedTabsWithoutTwitter = customizedTabs.filter(
      (item) => item !== PostEditorTabsTypes.TWITTER
    );
    const tabsConfig = {
      maxMessageCharsTwitter: getMaxMessageChars(PostEditorTabsTypes.TWITTER),
      maxMessageChars: undefined,
      maxMentions: undefined,
      maxHashtags: undefined,
      maxVideos: undefined,
      maxPhotos: undefined,
      maxComments: undefined,
      scraperEnabled: undefined,
      scraperAllowEditDetails: false,
      carouselEnabled: false,
      carouselMaxItems: undefined,
      messageLinkPreview: false,
      highlightAtMentions: false,
      highlightHashtags: undefined,
      highlightLinks: undefined,
      mentionEnabled: false,
      mentionAllowSpaces: false,
    };

    const maxMessageCharsArray = customizedTabsWithoutTwitter.map((item) =>
      getMaxMessageChars(item)
    );

    if (
      customizedTabsWithoutTwitter.length &&
      !maxMessageCharsArray.includes(undefined)
    ) {
      tabsConfig.maxMessageChars = Math.max(...maxMessageCharsArray);
    }

    const maxMentionsArray = customizedTabsWithoutTwitter.map((item) =>
      getMaxMentions(item)
    );

    if (
      customizedTabsWithoutTwitter.length &&
      !maxMentionsArray.includes(undefined)
    ) {
      tabsConfig.maxMentions = Math.max(...maxMentionsArray);
    }

    const maxHashtagsArray = customizedTabsWithoutTwitter.map((item) =>
      getMaxHashtags(item)
    );

    if (
      customizedTabsWithoutTwitter.length &&
      !maxHashtagsArray.includes(undefined)
    ) {
      tabsConfig.maxHashtags = Math.max(...maxHashtagsArray);
    }

    const maxVideosArray = customizedTabs.map((item) => getMaxVideos(item));

    if (customizedTabs.length && !maxVideosArray.includes(undefined)) {
      tabsConfig.maxVideos = Math.max(...maxVideosArray);
    } else {
      tabsConfig.maxVideos = getBaseMaxVideos();
    }

    const maxPhotosArray = customizedTabs.map((item) => getMaxPhotos(item));

    if (customizedTabs.length && !maxPhotosArray.includes(undefined)) {
      tabsConfig.maxPhotos = Math.max(...maxPhotosArray);
    } else {
      tabsConfig.maxPhotos = getBaseMaxPhotos();
    }

    const maxPhotosVideosArray = customizedTabs.map((item) =>
      getMaxPhotosVideos(item)
    );

    if (
      maxPhotosVideosArray.length &&
      !maxPhotosVideosArray.includes(undefined)
    ) {
      tabsConfig.maxPhotosVideos = Math.max(...maxPhotosVideosArray);
    } else {
      tabsConfig.maxPhotosVideos = getBaseMaxPhotosVideos();
    }

    const maxCommentsArray = customizedTabs.map((item) => getMaxComments(item));

    if (customizedTabs.length && !maxCommentsArray.includes(undefined)) {
      tabsConfig.maxComments = Math.max(...maxCommentsArray);
    } else {
      tabsConfig.maxComments = getBaseMaxComments();
    }

    tabsConfig.scraperEnabled =
      !customizedTabs.length ||
      customizedTabs.map((item) => canAddScraper(item)).includes(true);
    tabsConfig.highlightHashtags =
      !customizedTabs.length ||
      customizedTabs
        .map((item) => haveHighlightedHashtags(item))
        .includes(true);
    tabsConfig.highlightLinks =
      !customizedTabs.length ||
      customizedTabs.map((item) => haveHighlightedLinks(item)).includes(true);

    return tabsConfig;
  }

  return {
    maxMessageCharsTwitter: getMaxMessageChars(PostEditorTabsTypes.TWITTER),
    maxMessageChars: getMaxMessageChars(tab, postType),
    maxMentions: getMaxMentions(tab, postType),
    maxHashtags: getMaxHashtags(tab, postType),
    maxVideos: getMaxVideos(tab, postType),
    maxPhotos: getMaxPhotos(tab, postType),
    maxPhotosVideos: getMaxPhotosVideos(tab, postType),
    maxComments: getMaxComments(tab, postType),
    scraperEnabled: canAddScraper(tab, postType),
    scraperAllowEditDetails: canEditScraperDetails(tab, postType),
    carouselEnabled: canAddCarousel(tab, postType),
    carouselMaxItems: getMaxCarouselItems(tab, postType),
    messageLinkPreview: !canAddScraper(tab, postType) && haveMessageLinkPreview(tab, postType),
    highlightAtMentions: haveHighlightedAtMentions(tab, postType),
    highlightHashtags: haveHighlightedHashtags(tab, postType),
    highlightLinks: haveHighlightedLinks(tab, postType),
    mentionEnabled: canAddMentions(tab, postType),
    mentionAllowSpaces: canAddMentionsWithSpaces(tab, postType),
  };
};

export const postMediaToQuickMedia = (type, item) => ({
  id: item.url,
  type,
  url: item.url,
  thumbUrl: item.thumbUrl,
  data: cloneDeep(item),
});

export const isBaseTabEmpty = (data) =>
  !data?.message &&
  !data?.photos?.length &&
  !data?.videos?.length &&
  !data?.comments?.length &&
  !data?.scraper?.link;

export const isQuickMediaFileInProgress = (item) =>
  item.progress > 0 || item.progress === 0;

export const getCustomizedTimesByDelay = (
  accountsIds,
  date,
  time,
  timezone,
  delayUnit,
  delayValue
) => {
  const data = {};

  if (!date || !time) {
    accountsIds.forEach((accountId) => {
      data[accountId] = {
        date: "",
        time: "",
        timezone,
      };
    });

    return data;
  }

  let nextDateTime = parseISODate(`${date} ${time}`);

  const addByUnit = (dateTime, unit, value) => {
    switch (unit) {
      case "minutes":
        return addMinutes(dateTime, value);
      case "hours":
        return addHours(dateTime, value);
      case "days":
        return addDays(dateTime, value);
      default:
        return dateTime;
    }
  };

  accountsIds.forEach((accountId) => {
    data[accountId] = {
      date: formatDate(nextDateTime, "yyyy-MM-dd", dateFnsOptions()),
      time: formatDate(nextDateTime, "HH:mm", dateFnsOptions()),
      timezone,
    };
    nextDateTime = addByUnit(nextDateTime, delayUnit, delayValue);
  });

  return data;
};

export const getMediaFileMeta = async (type, url) => {
  try {
    const meta = {};

    if (type === "image") {
      const image = await getImageMeta(url);

      if (image) {
        meta.width = image.width;
        meta.height = image.height;
      }
    } else if (type === "video") {
      const video = await getVideoMeta(url);

      if (video) {
        meta.width = video.videoWidth;
        meta.height = video.videoHeight;
        meta.duration = video.duration;
      }
    }

    return meta;
  } catch (error) {
    return {};
  }
};
